import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  CreditCard, Check, Sparkles, Users, Building2, TrendingUp, Clock, AlertTriangle,
} from "lucide-react";

/**
 * Billing card on Admin -> Settings.
 *
 * Shows the org's current plan + usage counters + trial state, and lets
 * the admin start a Stripe checkout to upgrade / change plan.  On return
 * from Stripe (?billing=success&session_id=...) we call
 * /api/billing/session/{sid} to flip the org to the paid tier.
 */
export function BillingCard() {
  const [status, setStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [cycle, setCycle] = useState("monthly");
  const [busy, setBusy] = useState(false);
  const [checkoutFor, setCheckoutFor] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.get("/billing/status"), api.get("/billing/plans")]);
      setStatus(s.data);
      setPlans(p.data.plans || []);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Handle return from Stripe checkout
  useEffect(() => {
    const flag = searchParams.get("billing");
    const sid = searchParams.get("session_id");
    if (flag === "success" && sid) {
      api.post(`/billing/session/${sid}`).then(({ data }) => {
        if (data.ok) {
          toast.success(`Plan activated: ${data.plan}`);
          load();
        } else {
          toast.warning(`Checkout status: ${data.status}`);
        }
      }).catch((e) => toast.error(apiErrorMessage(e)));
      searchParams.delete("billing");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    } else if (flag === "cancel") {
      toast.info("Checkout canceled - no changes applied.");
      searchParams.delete("billing");
      setSearchParams(searchParams, { replace: true });
    } else if (flag === "start") {
      // Coming from signup with a paid plan chosen - noop; user will click Upgrade
      searchParams.delete("billing");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, load]);

  const upgrade = async (planId) => {
    setBusy(true);
    setCheckoutFor(planId);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", {
        plan_id: planId, billing_cycle: cycle, origin_url: origin,
      });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
    setCheckoutFor(null);
  };

  const cancel = async () => {
    if (!window.confirm("Cancel the subscription at end of the current billing period? Your seats + properties stay live until then.")) return;
    setBusy(true);
    try {
      await api.post("/billing/cancel");
      toast.success("Cancellation scheduled - subscription ends at period close");
      await load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  if (!status) {
    return (
      <Card className="border-white/10 bg-card p-5" data-testid="billing-card">
        <div className="text-sm text-zinc-400">Loading billing status...</div>
      </Card>
    );
  }
  const { org, plan, usage, at_limit } = status;
  const seatPct = Math.min(100, (usage.seats / plan.seat_limit) * 100);
  const propPct = Math.min(100, (usage.properties / plan.property_limit) * 100);

  return (
    <Card className="border-white/10 bg-card p-5" data-testid="billing-card">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline flex items-center gap-1">
            <CreditCard className="h-3 w-3" /> Billing &amp; Plan
          </div>
          <div className="text-lg font-semibold font-display mt-1">
            {org.name || "Your organization"}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="border-white/10 text-zinc-200">{plan.name}</Badge>
          {org.billing_status === "trial" && (
            <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10">
              Trial - {org.trial_days_left ?? "?"}d left
            </Badge>
          )}
          {org.billing_status === "active" && (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
              Active
            </Badge>
          )}
          {org.billing_status === "canceling" && (
            <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10">
              Cancels at period end
            </Badge>
          )}
          {org.billing_status === "grandfathered" && (
            <Badge variant="outline" className="border-indigo-500/40 text-indigo-300 bg-indigo-500/10">
              Grandfathered demo
            </Badge>
          )}
        </div>
      </div>

      {org.trial_expired && (
        <div className="mt-3 flex items-center gap-2 text-xs text-rose-300 border border-rose-500/40 bg-rose-500/10 rounded-md px-3 py-2">
          <AlertTriangle className="h-3 w-3" /> Your trial has ended. Upgrade below to keep every feature.
        </div>
      )}

      {/* Usage bars */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="billing-usage">
        <div className="rounded-md border border-white/10 p-3">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-1">
            <Users className="h-3 w-3" /> Seats
          </div>
          <div className="text-lg font-semibold tabular-nums">
            {usage.seats} <span className="text-xs text-zinc-500">/ {plan.seat_limit >= 100000 ? "unlimited" : plan.seat_limit}</span>
          </div>
          {plan.seat_limit < 100000 && (
            <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className={"h-full " + (at_limit.seats ? "bg-rose-400" : "bg-orange-400")} style={{ width: `${seatPct}%` }} />
            </div>
          )}
        </div>
        <div className="rounded-md border border-white/10 p-3">
          <div className="text-[11px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 mb-1">
            <Building2 className="h-3 w-3" /> Properties
          </div>
          <div className="text-lg font-semibold tabular-nums">
            {usage.properties} <span className="text-xs text-zinc-500">/ {plan.property_limit >= 10000 ? "unlimited" : plan.property_limit}</span>
          </div>
          {plan.property_limit < 10000 && (
            <div className="mt-2 h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className={"h-full " + (at_limit.properties ? "bg-rose-400" : "bg-orange-400")} style={{ width: `${propPct}%` }} />
            </div>
          )}
        </div>
      </div>

      {/* Plan grid */}
      <div className="mt-5 flex items-center justify-between mb-3">
        <div className="overline flex items-center gap-1">
          <TrendingUp className="h-3 w-3" /> Change plan
        </div>
        <Tabs value={cycle} onValueChange={setCycle}>
          <TabsList className="bg-white/[0.03] border border-white/10 h-8">
            <TabsTrigger value="monthly" data-testid="cycle-monthly" className="text-xs px-3">Monthly</TabsTrigger>
            <TabsTrigger value="annual" data-testid="cycle-annual" className="text-xs px-3">Annual · save ~17%</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="billing-plans">
        {plans.map((p) => {
          const isCurrent = p.id === plan.id;
          const price = cycle === "annual" ? p.annual_price_cents : p.monthly_price_cents;
          const per = cycle === "annual" ? "/ year" : "/ month";
          return (
            <div
              key={p.id}
              className={
                "rounded-lg border p-4 flex flex-col " +
                (p.highlight ? "border-orange-500/60 bg-orange-500/[0.06]" : "border-white/10 bg-white/[0.02]")
              }
              data-testid={`billing-plan-${p.id}`}
            >
              <div className="flex items-center justify-between mb-1">
                <div className="text-base font-semibold font-display">{p.name}</div>
                {p.highlight && (
                  <Badge variant="outline" className="border-orange-500/40 text-orange-300 bg-orange-500/10">
                    <Sparkles className="h-3 w-3 mr-1" /> Popular
                  </Badge>
                )}
              </div>
              <div className="text-[11px] text-zinc-500 mb-2">{p.tagline}</div>
              <div className="flex items-baseline gap-1 mb-3">
                <span className="text-2xl font-bold">${(price / 100).toFixed(0)}</span>
                <span className="text-xs text-zinc-500">{per}</span>
              </div>
              <ul className="space-y-1 flex-1">
                {p.features.map((f, i) => (
                  <li key={i} className="text-[12px] text-zinc-300 flex items-start gap-1.5">
                    <Check className="h-3 w-3 text-emerald-400 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Button
                size="sm"
                disabled={isCurrent || busy}
                onClick={() => upgrade(p.id)}
                className={
                  "mt-4 w-full " +
                  (isCurrent
                    ? "bg-white/[0.05] text-zinc-300 border border-white/10"
                    : "bg-orange-500 hover:bg-orange-600")
                }
                data-testid={`billing-upgrade-${p.id}`}
              >
                {isCurrent
                  ? "Current plan"
                  : checkoutFor === p.id
                    ? "Redirecting..."
                    : (plans.findIndex((x) => x.id === plan.id) > plans.findIndex((x) => x.id === p.id)
                        ? "Switch to " + p.name
                        : "Upgrade to " + p.name)}
              </Button>
            </div>
          );
        })}
      </div>

      {org.billing_status === "active" && (
        <div className="mt-4 flex items-center gap-2">
          <Button variant="outline" size="sm" className="border-white/10" onClick={cancel} disabled={busy} data-testid="billing-cancel">
            <Clock className="h-3.5 w-3.5 mr-1" /> Cancel at period end
          </Button>
          <div className="text-[11px] text-zinc-500">You keep every feature until the current billing period closes.</div>
        </div>
      )}
    </Card>
  );
}
